import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ArrowLeft, Leaf, BookOpen, Plus, Loader2, Sparkles, Calendar, ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import "../styles/emergence.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmergenceJournal() {
  const { i18n } = useTranslation();
  const isFrench = i18n.language === 'fr';
  
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewEntry, setShowNewEntry] = useState(false);
  const [newEntryText, setNewEntryText] = useState("");
  const [saving, setSaving] = useState(false);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);

  useEffect(() => {
    // Vérifier si un email est stocké
    const storedEmail = localStorage.getItem("emergence_email");
    if (storedEmail) {
      setUserEmail(storedEmail);
      fetchEntries(storedEmail);
    } else {
      setShowEmailPrompt(true);
      setLoading(false);
    }
  }, []);

  const fetchEntries = async (email) => {
    try {
      const response = await axios.get(`${API}/emergence/journal?email=${encodeURIComponent(email)}`);
      setEntries(response.data.entries || []);
    } catch (error) {
      console.error("Error fetching journal:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetEmail = (email) => {
    localStorage.setItem("emergence_email", email);
    setUserEmail(email);
    setShowEmailPrompt(false);
    fetchEntries(email);
  };

  const handleSaveEntry = async () => {
    if (!newEntryText.trim()) return;
    setSaving(true);

    try {
      const response = await axios.post(`${API}/emergence/journal`, {
        email: userEmail,
        content: newEntryText.trim(),
        language: i18n.language
      });

      setEntries(prev => [response.data.entry, ...prev]);
      setNewEntryText("");
      setShowNewEntry(false);
      toast.success(isFrench ? "Entrée enregistrée !" : "Entry saved!");
    } catch (error) {
      console.error("Error saving entry:", error);
      toast.error(isFrench ? "Erreur de sauvegarde" : "Save error");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString(isFrench ? 'fr-FR' : 'en-US', options);
  };

  const journalPrompts = isFrench ? [
    "Comment je me sens aujourd'hui ?",
    "Qu'est-ce qui m'a apporté de la joie récemment ?",
    "Qu'est-ce qui me pèse en ce moment ?",
    "De quoi suis-je reconnaissant(e) ?",
    "Quelle petite victoire puis-je célébrer ?"
  ] : [
    "How am I feeling today?",
    "What brought me joy recently?",
    "What's weighing on me right now?",
    "What am I grateful for?",
    "What small victory can I celebrate?"
  ];

  if (loading) {
    return (
      <div className="emergence-bg min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#3B7A8C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="emergence-bg min-h-screen">
      {/* Header */}
      <header className="emergence-glass-header fixed top-0 left-0 right-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/emergence" className="flex items-center gap-2 text-[#3B7A8C] hover:opacity-80" data-testid="back-link">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">{isFrench ? "Retour" : "Back"}</span>
          </Link>
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#D4A574]" />
            <span className="font-heading text-lg text-[#2D5A66]">{isFrench ? "Mon journal" : "My journal"}</span>
          </div>
          <Button 
            onClick={() => setShowNewEntry(true)}
            className="emergence-btn-accent text-sm px-4 py-2"
            data-testid="new-entry-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {isFrench ? "Nouvelle entrée" : "New entry"}
          </Button>
        </div>
      </header>

      <main className="pt-28 pb-24 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Prompts d'inspiration */}
          <div className="mb-8 p-6 rounded-2xl bg-white/50 border border-[#3B7A8C]/10">
            <p className="text-sm font-medium text-[#2D5A66] mb-3">
              💡 {isFrench ? "Inspiration pour écrire :" : "Writing inspiration:"}
            </p>
            <div className="flex flex-wrap gap-2">
              {journalPrompts.map((prompt, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setNewEntryText(prompt + "\n\n");
                    setShowNewEntry(true);
                  }}
                  className="px-3 py-1.5 text-xs rounded-full bg-white border border-[#3B7A8C]/20 text-[#7A8B99] hover:border-[#3B7A8C] hover:text-[#3B7A8C] transition-all"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>

          {/* Liste des entrées */}
          {entries.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen className="w-16 h-16 text-[#3B7A8C]/20 mx-auto mb-4" />
              <h3 className="font-heading text-xl text-[#2D5A66] mb-2">
                {isFrench ? "Ton journal est vide" : "Your journal is empty"}
              </h3>
              <p className="text-[#7A8B99] mb-6">
                {isFrench 
                  ? "Commence à écrire pour capturer tes pensées et suivre ton évolution"
                  : "Start writing to capture your thoughts and track your growth"}
              </p>
              <Button onClick={() => setShowNewEntry(true)} className="emergence-btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                {isFrench ? "Écrire ma première entrée" : "Write my first entry"}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {entries.map((entry, idx) => (
                <Card 
                  key={entry.id || idx} 
                  className="emergence-card border-0 cursor-pointer"
                  onClick={() => setExpandedEntry(expandedEntry === idx ? null : idx)}
                  data-testid={`journal-entry-${idx}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 text-[#7A8B99] text-sm">
                        <Calendar className="w-4 h-4" />
                        {formatDate(entry.created_at)}
                      </div>
                      {expandedEntry === idx ? (
                        <ChevronUp className="w-5 h-5 text-[#7A8B99]" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-[#7A8B99]" />
                      )}
                    </div>
                    
                    <p className={`text-[#3D4F5F] leading-relaxed ${expandedEntry === idx ? '' : 'line-clamp-3'}`}>
                      {entry.content}
                    </p>

                    {entry.ai_insight && expandedEntry === idx && (
                      <div className="mt-4 p-4 rounded-xl bg-gradient-to-br from-[#3B7A8C]/5 to-[#8FB8A8]/5 border border-[#3B7A8C]/10">
                        <div className="flex items-center gap-2 text-[#3B7A8C] text-sm font-medium mb-2">
                          <Sparkles className="w-4 h-4" />
                          {isFrench ? "Réflexion du coach" : "Coach reflection"}
                        </div>
                        <p className="text-[#5C6B7A] text-sm leading-relaxed">
                          {entry.ai_insight}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Modal nouvelle entrée */}
      <Dialog open={showNewEntry} onOpenChange={setShowNewEntry}>
        <DialogContent className="sm:max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#2D5A66]">
              {isFrench ? "📝 Nouvelle entrée" : "📝 New entry"}
            </DialogTitle>
            <DialogDescription className="text-[#7A8B99]">
              {isFrench 
                ? "Écris librement, sans te censurer. C'est ton espace."
                : "Write freely, without censoring. This is your space."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={newEntryText}
              onChange={(e) => setNewEntryText(e.target.value)}
              placeholder={isFrench ? "Qu'est-ce qui te traverse l'esprit aujourd'hui ?" : "What's on your mind today?"}
              className="min-h-[200px] rounded-xl border-2 border-[#3B7A8C]/20 focus:border-[#3B7A8C] resize-none"
              data-testid="journal-textarea"
            />
            <div className="flex justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowNewEntry(false)}
                className="rounded-full border-[#3B7A8C]/20"
              >
                {isFrench ? "Annuler" : "Cancel"}
              </Button>
              <Button 
                onClick={handleSaveEntry}
                disabled={!newEntryText.trim() || saving}
                className="emergence-btn-primary"
                data-testid="save-entry-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                {isFrench ? "Enregistrer" : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal email */}
      <Dialog open={showEmailPrompt} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md bg-white" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#2D5A66] text-center">
              {isFrench ? "🌱 Ton journal personnel" : "🌱 Your personal journal"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#7A8B99] text-center">
              {isFrench 
                ? "Entre ton email pour retrouver ton journal à chaque visite"
                : "Enter your email to access your journal on each visit"}
            </p>
            <input
              type="email"
              placeholder={isFrench ? "Ton email" : "Your email"}
              className="w-full h-12 px-4 rounded-xl border-2 border-[#3B7A8C]/20 focus:border-[#3B7A8C] focus:outline-none"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && e.target.value) {
                  handleSetEmail(e.target.value);
                }
              }}
              data-testid="email-input"
            />
            <Button 
              onClick={(e) => {
                const input = e.target.closest('.space-y-4').querySelector('input');
                if (input.value) handleSetEmail(input.value);
              }}
              className="w-full emergence-btn-accent"
              data-testid="set-email-btn"
            >
              {isFrench ? "Accéder à mon journal" : "Access my journal"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
